<template>
  <div
    ref="superHero"
    v-editable="blok"
    class="super-hero"
    :class="animateHero && 'super-hero--animate'"
  >
    <div class="super-hero__media">
      <div class="super-hero__image-large-container">
        <Picture
          v-if="blok.large_image && blok.large_image.filename"
          v-editable="blok"
          class="super-hero__image-large"
          :blok="makePicture(blok.large_image)"
          loading="eager"
          :height="768"
          :width="576"
        />
      </div>
      <Picture
        v-if="blok.small_image && blok.small_image.filename"
        v-editable="blok"
        class="super-hero__image-small"
        :blok="makePicture(blok.small_image)"
        loading="eager"
        :height="320"
        :width="240"
      />
    </div>
    <div class="super-hero__content">
      <CustomHeading
        v-if="blok.text[0].heading && blok.text[0].heading[0]"
        :blok="blok.text[0].heading[0]"
      />
      <h1
        v-else
        ref="headingTag"
        class="super-hero__heading custom-heading"
        :class="[animate && 'animate']"
        aria-label="Transform the way you work"
      >
        <span aria-hidden="true">Transform</span>
        <span aria-hidden="true">the way</span>
        <span aria-hidden="true">
          you <span ref="drawnItem" class="drawing-circle-1">work</span>
        </span>
      </h1>
      <div class="super-hero__text-cta">
        <RichTextEditor :blok="{ text: blok.text[0].text }" />
        <CTAGroup v-if="blok.ctas" :ctas="blok.ctas" />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ImageBlokProps, SuperHeroBlokProps } from '@/types';

// Plugins
const { $noMotionPreference } = useNuxtApp();

// Props
defineProps<{
  blok: SuperHeroBlokProps;
}>();

const makePicture = (src: ImageBlokProps) => ({
  image: src,
});

// Manage Hand-drawn Elements
const animate = ref(false);
const headingTag = ref<HTMLHeadingElement>();
const drawnItem = ref<NodeListOf<HTMLSpanElement>>();
drawIt(animate, headingTag, drawnItem);

// Intersection Observer Variables & refs
let observer: IntersectionObserver;
const animateHero = ref(false);
const superHero = ref<HTMLDivElement>();

// Lifecycle : onMounted
onMounted(() => {
  // Only set up IO if no motion preference and there's an item to animate
  if ($noMotionPreference === true && superHero.value) {
    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting === true) {
          animateHero.value = true;
        }
      });
    });

    observer.observe(superHero.value);
  }
});

// Lifecycle : onBeforeUnmount
onBeforeUnmount(() => {
  if (observer) observer.disconnect();
});
</script>

<style lang="scss">
.super-hero {
  margin-inline: auto;
  width: min(var(--content-width), 100%);

  @media all and #{$mq-m-min} {
    display: grid;
    grid-template-columns: auto 1fr;
  }

  /* Container for Images */
  &__media {
    display: flex;
    height: fit-content;
    position: relative;
    width: fit-content;
    z-index: 2;

    @media all and #{$mq-m-min} {
      margin-block: auto;
      margin-inline: 0;
    }

    @media all and #{$mq-l-min} {
      margin-block: auto 0;
      margin-inline: 0;
    }
  }

  &__image-large-container {
    height: fit-content;
  }

  /* Large Image */
  &__image-large {
    align-self: flex-end;
    transition: transform var(--timing) var(--easing-1);
    width: columns(9);

    @media all and #{$mq-m-min} {
      width: columns(10);

      img {
        object-fit: cover;
      }
    }

    @media all and #{$mq-l-min} {
      width: columns(9);
    }
  }

  /* Small Image */
  &__image-small {
    /* Styling to offset image and ensure scaling. */
    @media all and #{$mq-s-max} {
      inset-block-start: 50%;
      inset-inline-end: columns(-5);
      margin-block: auto;
      transform: translateY(-50%);
      width: columns(6);
    }

    @media all and #{$mq-m-min} {
      inset-block-end: -40px;
      inset-inline-end: columns(1);
      width: columns(4);
    }

    @media all and #{$mq-l-min} {
      inset-block-end: -95px;
      inset-inline-end: columns(-3);
    }
  }

  /* Small Image : Bumping specificity for position */
  & .super-hero__image-small {
    position: absolute;
  }

  /* Container for Content */
  &__content {
    display: flex;
    flex-direction: column;
    padding-block: var(--spacing-l);
    padding-inline: var(--side-spacing);

    @media all and #{$mq-m-min} {
      align-items: flex-end;
      justify-content: center;
      padding-inline-start: columns(0.5);
    }
    @media all and #{$mq-l-min} {
      padding-inline-end: var(--side-spacing);
    }
  }

  /* Adjust Styling of CustomHeading Component */
  .custom-heading:not(.super-hero__heading) {
    width: columns(12);
  }

  /* Baked-In Heading with Custom Layout : Only shown if CustomHeading isn't defined. */
  &__heading {
    display: flex;
    flex-direction: column;
    text-align: end;

    > span {
      display: inline-block;
      padding-inline-end: var(--offset, 0);
    }

    @media all and #{$mq-s-max} {
      margin-inline: auto;

      > span:nth-child(1) {
        --offset: 20px;
      }

      > span:nth-child(3) {
        --offset: 25px;
      }
    }

    @media all and #{$mq-m-min} {
      align-self: flex-end;
      width: min(750px, 100%);

      > span:nth-child(1) {
        --offset: 40px;
      }

      > span:nth-child(3) {
        --offset: 50px;
      }
    }
  }

  /* Container for Text & CTA */
  &__text-cta {
    position: relative;
    z-index: 3;

    @media all and #{$mq-m-min} {
      margin-inline-end: columns(1);
      max-width: columns(11);
    }

    @media all and #{$mq-l-min} {
      max-width: columns(7);
    }

    /* Adjust Styling of CustomRTE Component */
    .rte {
      margin-block-start: var(--spacing-m);
    }
  }

  /* Adjust Styling of CTAGroup Component */
  .cta-group {
    margin-block-start: var(--spacing-s);

    @media all and #{$mq-s-max} {
      justify-content: start;
    }
  }
}

@media screen and (prefers-reduced-motion: no-preference) {
  .super-hero {
    opacity: 0;
  }

  .super-hero--animate {
    --timing-heading-line: 792ms;
    --timing-small-image-delay: var(--timing-large-image-fade);
    --timing-heading-delay: calc(
      var(--timing-large-image-fade) + var(--timing-small-image-wipe)
    );
    --timing-heading-line-1-delay: calc(
      var(--timing-large-image-fade) + var(--timing-small-image-wipe)
    );
    --timing-heading-line-2-delay: calc(
      var(--timing-heading-line-1-delay) + 100ms
    );
    --timing-heading-line-3-delay: calc(
      var(--timing-heading-line-2-delay) + 100ms
    );
    --timing-text-cta-fade-delay: calc(
      var(--timing-heading-line-3-delay) + 250ms
    );

    opacity: 1;
    transition: opacity 0ms linear var(--timing-hero-start-delay);

    .super-hero__image-large-container {
      animation: wipe-right var(--timing-large-image-wipe) var(--easing-2)
        forwards;
      animation-delay: var(--timing-hero-start-delay);
      will-change: clip-path;
    }

    .super-hero__image-large {
      animation: scale var(--timing-large-image-scale) var(--easing-2),
        fade-in var(--timing-large-image-fade) var(--easing-3);
      animation-delay: var(--timing-hero-start-delay);
      animation-fill-mode: forwards;
      opacity: 0;
      will-change: transform, opacity, clip-path;
    }

    .super-hero__image-small {
      animation: wipe-left var(--timing-small-image-wipe) var(--easing-2),
        fade-in var(--timing-small-image-fade) var(--easing-3);
      animation-delay: var(--timing-small-image-delay);
      animation-fill-mode: forwards;
      opacity: 0;
      will-change: clip-path, opacity;
    }

    .super-hero__heading {
      > span:nth-of-type(odd) {
        animation: slide-right var(--timing-heading-line) var(--easing-2),
          fade-in var(--timing-heading-line) var(--easing-3);
        animation-fill-mode: forwards;
        opacity: 0;
        transform: translateX(-5%);
      }

      > span:nth-of-type(even) {
        animation: slide-left 792ms var(--easing-2),
          fade-in 792ms var(--easing-3);
        animation-fill-mode: forwards;
        opacity: 0;
        transform: translateX(5%);
      }

      > span:nth-of-type(1) {
        animation-delay: var(--timing-heading-line-1-delay);
      }

      > span:nth-of-type(2) {
        animation-delay: var(--timing-heading-line-2-delay);
      }

      > span:nth-of-type(3) {
        animation-delay: var(--timing-heading-line-3-delay);
      }
    }

    .custom-heading:not(.super-hero__heading) {
      animation: wipe-left var(--timing-heading-wipe) var(--easing-2),
        scale var(--timing-heading-scale) var(--easing-2),
        fade-in var(--timing-heading-fade) var(--easing-3);
      animation-delay: var(--timing-heading-line-1-delay);
      animation-fill-mode: forwards;
      opacity: 0;
      will-change: clip-path, transform, opacity;
    }

    .super-hero__text-cta {
      animation: fade-in var(--timing-text-cta-fade) var(--easing-3)
        var(--timing-text-cta-fade-delay) forwards;
      opacity: 0;
      will-change: opacity;
    }
  }
}
</style>
